// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../firebase';  // ✅ NEW: Added db import
import { doc, getDoc } from 'firebase/firestore';  // ✅ NEW: Added Firestore imports
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { User, LogOut, MessageCircle, Trophy, BarChart3, Settings, BookOpen } from 'lucide-react';
import ProfileCompletionModal from '../components/ProfileCompletionModal';
import { isProfileComplete } from '../config/locationData';
import { isAdmin } from '../config/adminConfig';

function Home() {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [unreadReportCount, setUnreadReportCount] = useState(0);  // ✅ NEW: Unread report count
  const navigate = useNavigate();

  const [userProfile, setUserProfile] = useState(null);

  // Load the Firestore user doc so we can check whether contact info is complete.
  const loadUserProfile = async (uid) => {
    if (!uid) return;
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) setUserProfile(snap.data());
    } catch (err) {
      console.error('Home: failed to load user profile for contact gate:', err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/login", { replace: true });
      } else {
        setUser(currentUser);
        loadUserProfile(currentUser.uid);
      }
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, [navigate]);

  // ✅ NEW: Fetch unread report card count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let count = 0;
          
          if (userData.children && Array.isArray(userData.children)) {
            userData.children.forEach(child => {
              if (child.reportCards) {
                child.reportCards.forEach(rc => {
                  if (!rc.viewedAt) count++;
                });
              }
            });
          }
          
          setUnreadReportCount(count);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    
    fetchUnreadCount();
  }, [user]);

  // Update time every minute for dynamic greeting
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("user");
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getUserName = () => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0]; // First name only
    }
    if (user?.email) {
      return user.email.split('@')[0]; // Username from email
    }
    return "Friend";
  };

  if (!isAuthReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-indigo-600 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full text-center">
          <div className="text-6xl mb-4 animate-spin">🌟</div>
          <div className="text-purple-600 text-2xl font-bold animate-pulse">Loading your adventure...</div>
        </div>
      </div>
    );
  }

  const GameCard = ({ to, icon, title, description, color, comingSoon = false }) => (
    <div className="group">
      {comingSoon ? (
        <div className={`relative bg-gradient-to-br ${color} rounded-2xl p-6 shadow-xl border-4 border-white opacity-60 cursor-not-allowed transition-all duration-300`}>
          <div className="absolute inset-0 bg-black bg-opacity-20 rounded-2xl flex items-center justify-center">
            <span className="bg-yellow-400 text-yellow-800 px-4 py-2 rounded-full font-bold text-sm">
              🚧 Coming Soon! 🚧
            </span>
          </div>
          <div className="text-4xl mb-3">{icon}</div>
          <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
          <p className="text-white/80 text-sm">{description}</p>
        </div>
      ) : (
        <Link to={to} className="block">
          <div className={`bg-gradient-to-br ${color} rounded-2xl p-6 shadow-xl border-4 border-white hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group-hover:border-yellow-300`}>
            <div className="text-4xl mb-3 group-hover:animate-bounce">{icon}</div>
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
            <p className="text-white/80 text-sm">{description}</p>
            <div className="mt-4 bg-white/20 rounded-full px-4 py-2 text-white text-xs font-bold backdrop-blur-sm">
              ▶️ Play Now
            </div>
          </div>
        </Link>
      )}
    </div>
  );

  const CategorySection = ({ icon, title, children }) => (
    <div className="mb-12">
      <div className="flex items-center justify-center mb-6">
        <div className="bg-white rounded-2xl p-4 shadow-lg border-4 border-yellow-300">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{icon}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-purple-600">{title}</h2>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-indigo-600 p-4">
      {/* Contact-info gate for active users (Home only renders for active accounts).
          Staff are exempt; already-complete profiles never see this. */}
      {userProfile && !isAdmin(user?.uid) && !isProfileComplete(userProfile) && (
        <ProfileCompletionModal
          userData={userProfile}
          onComplete={() => loadUserProfile(user?.uid)}
        />
      )}
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-2xl border-4 border-yellow-300 mb-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h1 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
                  {getGreeting()}, {getUserName()}! 🌟
                </h1>
                <p className="text-lg md:text-xl text-gray-700 font-semibold">
                  Ready for another amazing Arabic adventure?
                </p>
                <div className="flex items-center justify-center md:justify-start gap-2 mt-2 text-gray-600">
                  <span className="text-2xl">🎯</span>
                  <span className="font-medium">Choose your learning journey below!</span>
                </div>
              </div>
              
              {/* User Profile Section */}
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-4 border-2 border-blue-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 w-12 h-12 rounded-full flex items-center justify-center">
                    <User className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{getUserName()}</p>
                    <p className="text-xs text-gray-600">{user?.email}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 md:gap-4">
                  <button 
                    onClick={() => navigate("/dashboard")}
                    className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    <BarChart3 size={16} />
                    <span>Progress Dashboard</span>
                  </button>

                  <button 
                    onClick={() => navigate("/resources")}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <BookOpen size={16} />
                    Resources
                  </button>

                  {/* ✅ UPDATED: Account Settings button with badge */}
                  <button 
                    onClick={() => navigate("/account")}
                    className="relative w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <Settings size={16} />
                    Account Hub
                    {unreadReportCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[24px] text-center shadow-lg">
                        {unreadReportCount}
                      </span>
                    )}
                  </button>

                  <button 
                    onClick={handleLogout}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Chat Section - Special Highlight */}
        <div className="mb-12">
          <Link to="/chat" className="block">
            <div className="bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-600 rounded-3xl p-8 shadow-2xl border-4 border-white hover:shadow-3xl transform hover:scale-105 transition-all duration-300 text-center">
              <div className="text-6xl mb-4 animate-bounce">🤖</div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-3">Teta Zeitouna AI Tutor</h2>
              <p className="text-white/90 text-lg mb-4">Chat with your personal Arabic AI teacher anytime!</p>
              <div className="bg-white/20 rounded-full px-6 py-3 text-white font-bold backdrop-blur-sm inline-block">
                <MessageCircle className="inline mr-2" size={20} />
                Start Conversation
              </div>
            </div>
          </Link>
        </div>

        {/* Word Learning Games */}
        <CategorySection icon="📚" title="Word Learning Games">
          <GameCard
            to="/wordmatch"
            icon="🎯"
            title="Match & Learn"
            description="Match Arabic words with their English meanings!"
            color="from-green-400 to-emerald-600"
          />
          <GameCard
            to="/audioquiz"
            icon="🎧"
            title="Sonic Safari"
            description="Listen and identify Arabic words by sound!"
            color="from-blue-400 to-blue-600"
          />
          <GameCard
            to="/arabichangman"
            icon="🪢"
            title="Arabic Hangman"
            description="Guess the Arabic word letter by letter!"
            color="from-purple-400 to-purple-600"
          />
          <GameCard
            to="/arabicmemorycards"
            icon="🧠"
            title="Memory Mirage"
            description="Test your memory with Arabic Words!"
            color="from-rose-400 to-pink-600"
          />
        </CategorySection>

        {/* Number Learning Games */}
        <CategorySection icon="🔢" title="Number Learning Games">
          <GameCard
            to="/arabicnumbersspeedgame"
            icon="⚡"
            title="Raqm Rush"
            description="Speed challenge with Arabic numbers!"
            color="from-orange-400 to-red-600"
          />
          <GameCard
            to="/arabicnumbersequencinggame"
            icon="📊"
            title="Sequence Master"
            description="Sort Arabic numbers from smallest to largest!"
            color="from-cyan-400 to-blue-600"
          />
          <GameCard
            to="/arabicnumbercardmatchinggame"
            icon="🎴"
            title="Flip & Find"
            description="Memory game with Arabic numerals!"
            color="from-indigo-400 to-purple-600"
          />
          <GameCard
            to="/arabicnumbersumtargetgame"
            icon="🎲"
            title="Sum Quest"
            description="Find numbers that add up to the target!"
            color="from-pink-400 to-red-600"
          />
          <GameCard
            to="/arabicnumberfishing"
            icon="🎣"
            title="Number Fishing"
            description="Catch the fish with the right Arabic number!"
            color="from-sky-400 to-cyan-600"
          />
        </CategorySection>

        {/* Alphabet & Letter Games */}
        <CategorySection icon="🔤" title="Alphabet & Letter Games">
          <GameCard
            to="/alphabettracing"
            icon="✍️"
            title="Letter Tracing"
            description="Practice writing Arabic letters!"
            color="from-teal-400 to-cyan-600"
          />
          <GameCard
            to="/lettersoundmatch"
            icon="🎵"
            title="Letter Sound Match"
            description="Match letters with their sounds!"
            color="from-violet-400 to-purple-600"
          />
        </CategorySection>

        {/* Geography & Culture Games */}
        <CategorySection icon="🌍" title="Geography & Culture">
          <GameCard
            to="/arabicflagguesser"
            icon="🏳️"
            title="Flag Master"
            description="Identify Arab country flags!"
            color="from-emerald-400 to-green-600"
          />
          <GameCard
            to="/biggersmallercountrygame"
            icon="🗺️"
            title="Country Comparison"
            description="Compare populations of Arab countries!"
            color="from-blue-400 to-indigo-600"
          />
          <GameCard
            icon="🏛️"
            title="Culture Quest"
            description="Explore Arabic culture and history!"
            color="from-amber-400 to-orange-600"
            comingSoon={true}
          />
          <GameCard
            to="/mapexplorer"
            icon="🗺️"
            title="Map Explorer"
            description="Find Arab countries on the map!"
            color="from-lime-400 to-green-600"
          />
        </CategorySection>

        {/* Coming Soon Section */}
        <CategorySection icon="🚀" title="Coming Soon">
          <GameCard
            icon="🎵"
            title="Music & Rhythm"
            description="Learn Arabic through songs and music!"
            color="from-violet-400 to-purple-600"
            comingSoon={true}
          />
          <GameCard
            icon="📖"
            title="Story Builder"
            description="Create your own Arabic stories!"
            color="from-sky-400 to-blue-600"
            comingSoon={true}
          />
          <GameCard
            icon="🎨"
            title="Art & Calligraphy"
            description="Beautiful Arabic calligraphy practice!"
            color="from-fuchsia-400 to-pink-600"
            comingSoon={true}
          />
        </CategorySection>

        {/* Footer */}
        <div className="text-center mt-12 mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border-4 border-yellow-300">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Trophy className="text-yellow-500" size={32} />
              <h3 className="text-2xl font-bold text-purple-600">Keep Learning!</h3>
              <Trophy className="text-yellow-500" size={32} />
            </div>
            <p className="text-gray-700 font-medium">
              Practice every day to become an Arabic master! 🌟
            </p>
            <div className="flex justify-center gap-4 mt-4 text-2xl">
              <span>🎯</span>
              <span>📚</span>
              <span>🏆</span>
              <span>🌟</span>
              <span>🎉</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;